import { useEffect, useState } from "react";
import { Palette, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CinematicStage, StageHeader, GlassCard, Spinner, cx } from "@/components/shared";

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-2">{label}</div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-white/5 border border-white/10 focus:border-[#5B4DFF]/60 rounded-xl p-3 outline-none text-white text-sm" />
    </div>
  );
}

function Select({ label, value, onChange, options, testid, getLabel = (o) => o, getValue = (o) => o }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-2">{label}</div>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 outline-none text-white text-sm">
        {options.map((o) => <option key={getValue(o)} value={getValue(o)} className="bg-[#0A1435]">{getLabel(o)}</option>)}
      </select>
    </div>
  );
}

export default function BrandStudio() {
  const [brand, setBrand] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/brand").then(({ data }) => setBrand(data)).catch(() => {});
    api.get("/catalogs").then(({ data }) => setCatalogs(data)).catch(() => {});
  }, []);

  const set = (k, v) => setBrand((b) => ({ ...b, [k]: v }));
  const setColor = (i, v) => setBrand((b) => { const c = [...(b.colors || [])]; c[i] = v; return { ...b, colors: c }; });

  const save = async () => {
    setSaving(true);
    try { await api.put("/brand", brand); toast.success("Brand memory saved — every new project inherits this."); }
    catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  if (!brand || !catalogs) return <Spinner label="Loading Brand Studio…" />;
  const colors = brand.colors || ["#081229", "#5B4DFF", "#FFB300"];

  return (
    <CinematicStage>
      <StageHeader stage="ID" room="The Brand Vault · Brand Studio" icon={Palette} title="The permanent memory of your studio"
        subtitle="Configure once. Every future project automatically inherits these settings."
        actions={
          <button onClick={save} disabled={saving} data-testid="brand-save-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FFB300] text-[#081229] font-semibold glow-gold disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Brand
          </button>
        } />

      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard className="p-6 space-y-5">
          <h3 className="font-display font-semibold flex items-center gap-2"><Palette className="w-4 h-4 text-[#5B4DFF]" /> Identity</h3>
          <Input label="Channel Name" value={brand.channel_name} onChange={(v) => set("channel_name", v)} placeholder="Vyom Creations" testid="brand-channel-name" />
          <Input label="Logo URL" value={brand.logo_url} onChange={(v) => set("logo_url", v)} placeholder="https://…" testid="brand-logo-url" />
          <Input label="Watermark URL" value={brand.watermark_url} onChange={(v) => set("watermark_url", v)} placeholder="https://…" testid="brand-watermark-url" />
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-2">Brand Colors</div>
            <div className="flex gap-3">
              {colors.map((c, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <input type="color" value={c} onChange={(e) => setColor(i, e.target.value)} data-testid={`brand-color-${i}`} className="w-12 h-12 rounded-xl bg-transparent cursor-pointer border border-white/10" />
                  <span className="font-mono text-[10px] text-slate-500">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6 space-y-5">
          <h3 className="font-display font-semibold">Creative Defaults</h3>
          <Select label="Preferred Voice" value={brand.preferred_voice} onChange={(v) => set("preferred_voice", v)} options={catalogs.voices} getLabel={(o) => `${o.name} · ${o.type}`} getValue={(o) => o.id} testid="brand-voice" />
          <Select label="Preferred Music" value={brand.preferred_music} onChange={(v) => set("preferred_music", v)} options={catalogs.music_styles} testid="brand-music" />
          <Select label="Preferred Style" value={brand.preferred_style} onChange={(v) => set("preferred_style", v)} options={catalogs.video_styles} testid="brand-style" />
          <Select label="Default Quality" value={brand.default_quality} onChange={(v) => set("default_quality", v)} options={catalogs.qualities} testid="brand-quality" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Intro line" value={brand.intro} onChange={(v) => set("intro", v)} placeholder="Welcome back to…" testid="brand-intro" />
            <Input label="Outro line" value={brand.outro} onChange={(v) => set("outro", v)} placeholder="Like & subscribe" testid="brand-outro" />
          </div>
        </GlassCard>

        <GlassCard className="p-6 space-y-5 lg:col-span-2">
          <h3 className="font-display font-semibold">Brand Memory 2.0</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Select label="Animation Style" value={brand.animation_style} onChange={(v) => set("animation_style", v)} options={catalogs.animation_styles} testid="brand-animation" />
            <Input label="Thumbnail Style" value={brand.thumbnail_style} onChange={(v) => set("thumbnail_style", v)} placeholder="Bold high-contrast" testid="brand-thumb-style" />
            <Input label="Caption Style" value={brand.caption_style} onChange={(v) => set("caption_style", v)} placeholder="Bottom center, animated" testid="brand-caption-style" />
          </div>
          <Input label="Brand Personality" value={brand.brand_personality} onChange={(v) => set("brand_personality", v)} placeholder="Warm, cinematic, inspiring" testid="brand-personality" />
          <MultiChips label="Preferred Platforms" options={["YouTube", "YouTube Shorts", "Instagram Reels", "Facebook Reels", "TikTok"]} value={brand.preferred_platforms || []} onChange={(v) => set("preferred_platforms", v)} testid="brand-platforms" />
          <MultiChips label="Preferred Aspect Ratios" options={catalogs.aspect_ratios.map((a) => a.id)} value={brand.preferred_aspect_ratios || []} onChange={(v) => set("preferred_aspect_ratios", v)} testid="brand-aspects" />
        </GlassCard>
      </div>
    </CinematicStage>
  );
}

function MultiChips({ label, options, value, onChange, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} onClick={() => toggle(o)} data-testid={`${testid}-${o}`}
            className={cx("px-3.5 py-2 rounded-xl text-sm border transition", value.includes(o) ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/60 text-white" : "bg-white/5 border-white/10 text-slate-300 hover:text-white")}>{o}</button>
        ))}
      </div>
    </div>
  );
}
